import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { ActiveUsersQueryVariables, UserFragment, UserRoleTypes, UserStatus, useActiveUsersQuery } from "../../graphql"
import { UsersList } from "./UsersList"

export type ActiveUsersProps = {
	keyword: string
	typeId?: string
	roleType?: UserRoleTypes
	status?: UserStatus
	onUserClick?: (user: UserFragment) => void
}

export const ActiveUsers: React.FC<ActiveUsersProps> = ({ keyword, typeId, roleType, status, onUserClick }) => {
	const [filter, setFilter] = useState({ typeId, roleType, keyword, status })

	const [pagination, setPagination] = useState<ActiveUsersQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useActiveUsersQuery({
		variables: { ...filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ typeId, roleType, keyword, status })
			setPagination((prev) => ({
				...prev,
				page: 1,
			}))
		}, 400)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [typeId, roleType, keyword, status])

	const nextPage = useCallback(() => {
		if (data?.activeUsers.hasNextPage && !fetching) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.activeUsers ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading users</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.activeUsers.users && data.activeUsers.users.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.activeUsers.users.length || 0} user
						{(data.activeUsers.users.length || 0) > 1 ? "s" : ""}
					</Text>

					<UsersList users={data.activeUsers.users} loadMore={nextPage} onUserClick={onUserClick} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more users</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any users.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
